import React, { Fragment, memo, useCallback, useState } from 'react'
import tw from 'twin.macro'
import { Tooltip, Typography } from '@mui/material'
import { colors } from 'src/theme'
import { useDispatch } from 'react-redux'
import { MCLiteAssignedSubcontractorsModal } from './MCLiteAssignedSubcontractorsModal'
import { Button } from 'src/components/shared/Button'
import { GSIcon } from 'src/components/shared/GSIcon'
import { SensitiveActionModal } from 'src/components/shared/SensitiveActionModal'
import UnassignImage from 'src/assets/images/unassign.svg'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { UnassignMCLiteToSubcontractors } from 'src/constants/actionTypes'
import { RelationContractor } from 'src/interfaces/mcLite'
import { showToast } from 'src/utils/toast'
import { handleApiError } from 'src/utils/errorHandlers'
import { RemovableMCLiteSubcontractor } from './RemovableMCLiteSubcontractor'
import { UserPermission } from 'src/constants/user'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { useModalOpen } from 'src/hooks/useModalOpen'
import { SUBCONTRACTOR_HAS_UNPROCESSED_PAYMENTS } from 'src/constants/mcLite'
import { UnpaidPaymentsDetectedModal } from './UnpaidPaymentsDetectedModal'

interface Props {
  contractorId: string
  className?: any
  isNotAllowed?: boolean
  assignedSubcontractors: RelationContractor[]
  onRefresh: () => void | Promise<void>
}

const MCLiteAssignedSubcontractorsDataRowComponent = ({
  contractorId,
  className,
  assignedSubcontractors,
  isNotAllowed,
  onRefresh,
}: Props) => {
  const dispatch = useDispatch()

  const { hasPermission } = useCheckPermissions()

  const [subcontractorsToUnassign, setSubcontractorsToUnassign] = useState<RelationContractor[]>([])

  const {
    isOpen: isAssignModalOpen,
    open: openAssignModal,
    close: closeAssignModal,
  } = useModalOpen()
  const {
    isOpen: isUnassignModalOpen,
    open: openUnassignModal,
    close: closeUnassignModal,
  } = useModalOpen()
  const {
    isOpen: isUnassignAllModalOpen,
    open: openUnassignAllModal,
    close: closeUnassignAllModal,
  } = useModalOpen()
  const {
    isOpen: isUnpaidPaymentsDetectedModalOpen,
    open: openUnpaidPaymentsDetectedModal,
    close: closeUnpaidPaymentsDetectedModal,
  } = useModalOpen()

  const handleUnassign = useCallback(async () => {
    try {
      await createAsyncAction(
        dispatch,
        UnassignMCLiteToSubcontractors.request({
          mcLiteId: contractorId,
          subcontractorIds: subcontractorsToUnassign.map((item) => item.id),
        }),
      )

      onRefresh()

      showToast('Subcontractor(s) unassigned.', { variant: 'success' })
    } catch (err: any) {
      if (err === SUBCONTRACTOR_HAS_UNPROCESSED_PAYMENTS) {
        closeUnassignModal()
        closeUnassignAllModal()
        setSubcontractorsToUnassign([])

        openUnpaidPaymentsDetectedModal()

        return
      }

      handleApiError(err)
    }

    closeUnassignModal()
    closeUnassignAllModal()
    setSubcontractorsToUnassign([])
  }, [contractorId, assignedSubcontractors, subcontractorsToUnassign, onRefresh])

  const handleUnassignOne = useCallback((subcontractor: RelationContractor) => {
    setSubcontractorsToUnassign([subcontractor])
    openUnassignModal()
  }, [])

  const handleUnassignAll = useCallback(async () => {
    setSubcontractorsToUnassign(assignedSubcontractors)
    openUnassignAllModal()
  }, [assignedSubcontractors, handleUnassign])

  return (
    <div>
      <div
        css={[
          tw`py-[14px] border-0 border-t border-solid border-[#EDEDED] flex items-center justify-between`,
          className,
        ]}
      >
        <div css={[tw`flex w-full flex-col gap-1.5 max-w-[204px]`]}>
          <Typography color={colors.SHADES_GREY_400} variant="h6">
            Subcontractors
          </Typography>
          <Typography style={{ wordWrap: 'break-word' }} css={tw`text-black`} variant="body2">
            {`${assignedSubcontractors.length} assigned`}
          </Typography>
        </div>
        <div css={tw`flex gap-2 flex-row ml-4`}>
          {!!assignedSubcontractors.length && hasPermission(UserPermission.McLiteDelete) && (
            <Button
              color="grey"
              size="small"
              leftIcon={<GSIcon iconName="gs_circle_x_1" />}
              className={tw`min-w-[36px]`}
              onClick={handleUnassignAll}
            />
          )}
          {hasPermission(UserPermission.McLiteEdit) && (
            <Tooltip
              disableHoverListener={!isNotAllowed}
              title={
                <Fragment>
                  <Typography css={tw`mb-2`} variant="h6" color={colors.SHADES_GREY_200}>
                    Subcontractor Restriction
                  </Typography>
                  <Typography variant="body2" color={colors.SHADES_GREY_300}>
                    This contractor is already assigned to a Master Contractor. They cannot have
                    subcontractors assigned to them. To assign subcontractors, please unassign this
                    contractor from their current Master Contractor first.
                  </Typography>
                </Fragment>
              }
              color="black"
            >
              <span>
                <Button
                  color="grey"
                  size="small"
                  disabled={isNotAllowed}
                  leftIcon={<GSIcon iconName="gs_circle_plus" />}
                  className={tw`min-w-[36px]`}
                  onClick={openAssignModal}
                />
              </span>
            </Tooltip>
          )}
        </div>
      </div>
      {!!assignedSubcontractors.length && (
        <div css={tw`flex flex-wrap gap-2 border-0 border-t border-solid border-[#EDEDED] py-4`}>
          {assignedSubcontractors.map((item) => (
            <RemovableMCLiteSubcontractor
              key={item.id}
              subcontractor={item}
              hideRemove={!hasPermission(UserPermission.McLiteDelete)}
              onRemove={handleUnassignOne}
            />
          ))}
        </div>
      )}
      <MCLiteAssignedSubcontractorsModal
        contractorId={contractorId}
        isOpen={isAssignModalOpen}
        onClose={closeAssignModal}
        onSubmit={onRefresh}
      />
      <SensitiveActionModal
        isOpen={isUnassignAllModalOpen}
        submitLabel="Unassign"
        title="Unassign All Subcontractor"
        text="Are you sure you want to unassign All Subcontractors?"
        image={<img src={UnassignImage} />}
        onSubmit={handleUnassign}
        onClose={closeUnassignAllModal}
      />
      <SensitiveActionModal
        isOpen={isUnassignModalOpen}
        submitLabel="Unassign"
        title="Unassign Subcontractor"
        text="Are you sure you want to unassign Subcontractor?"
        image={<img src={UnassignImage} />}
        onSubmit={handleUnassign}
        onClose={closeUnassignModal}
      />
      <UnpaidPaymentsDetectedModal
        isOpen={isUnpaidPaymentsDetectedModalOpen}
        onClose={closeUnpaidPaymentsDetectedModal}
      />
    </div>
  )
}

export const MCLiteAssignedSubcontractorsDataRow = memo(
  MCLiteAssignedSubcontractorsDataRowComponent,
)
